export type CellType = ' ' | '#' | '@' | '+' | '$' | '*' | '.';

export interface Position {
  x: number;
  y: number;
}

export class Game {
  private matrix: CellType[][];
  private history: { matrix: CellType[][]; stepCount: number }[];
  private stepCount: number;
  public limit?: number;

  constructor(matrix: CellType[][], limit?: number) {
    this.matrix = matrix.map(row => [...row]);
    this.history = [];
    this.stepCount = 0;
    this.limit = limit;
  }

  static isValidValue(char: string): char is CellType {
    return [' ', '#', '@', '+', '$', '*', '.'].includes(char);
  }

  getMatrix(): CellType[][] {
    return this.matrix;
  }

  getWidth(): number {
    return Math.max(...this.matrix.map(row => row.length));
  }

  getHeight(): number {
    return this.matrix.length;
  }

  getContent(x: number, y: number): CellType {
    if (y < 0 || y >= this.matrix.length || x < 0 || x >= this.matrix[y].length) {
      return '#';
    }
    return this.matrix[y][x];
  }

  setContent(x: number, y: number, content: CellType): void {
    if (Game.isValidValue(content)) {
      this.matrix[y][x] = content;
    }
  }

  worker(): Position {
    for (let y = 0; y < this.matrix.length; y++) {
      for (let x = 0; x < this.matrix[y].length; x++) {
        if (this.matrix[y][x] === '@' || this.matrix[y][x] === '+') {
          return { x, y };
        }
      }
    }
    return { x: 0, y: 0 };
  }

  private next(x: number, y: number): CellType {
    const workerPos = this.worker();
    return this.getContent(workerPos.x + x, workerPos.y + y);
  }

  private canMove(x: number, y: number): boolean {
    const nextChar = this.next(x, y);
    return !['#', '*', '$'].includes(nextChar);
  }

  private canPush(x: number, y: number): boolean {
    const nextChar = this.next(x, y);
    const afterNext = this.getContent(
      this.worker().x + x + x,
      this.worker().y + y + y
    );
    return (nextChar === '*' || nextChar === '$') && (afterNext === ' ' || afterNext === '.');
  }

  isCompleted(): boolean {
    for (const row of this.matrix) {
      for (const cell of row) {
        if (cell === '$') {
          return false;
        }
      }
    }
    return true;
  }

  getStepCount(): number {
    return this.stepCount;
  }

  isFailed(): boolean {
    return this.limit !== undefined && this.stepCount >= this.limit && !this.isCompleted();
  }

  private moveBox(boxX: number, boxY: number, dx: number, dy: number): void {
    const current = this.getContent(boxX, boxY);
    const future = this.getContent(boxX + dx, boxY + dy);

    if (current === '$' && future === ' ') {
      this.setContent(boxX + dx, boxY + dy, '$');
      this.setContent(boxX, boxY, ' ');
    } else if (current === '$' && future === '.') {
      this.setContent(boxX + dx, boxY + dy, '*');
      this.setContent(boxX, boxY, ' ');
    } else if (current === '*' && future === ' ') {
      this.setContent(boxX + dx, boxY + dy, '$');
      this.setContent(boxX, boxY, '.');
    } else if (current === '*' && future === '.') {
      this.setContent(boxX + dx, boxY + dy, '*');
      this.setContent(boxX, boxY, '.');
    }
  }

  move(dx: number, dy: number, save = true): boolean {
    const workerPos = this.worker();
    const currentCell = this.getContent(workerPos.x, workerPos.y);

    if (this.canMove(dx, dy)) {
      const nextCell = this.next(dx, dy);

      if (save) this.history.push({ matrix: this.matrix.map(row => [...row]), stepCount: this.stepCount });

      if (currentCell === '@' && nextCell === ' ') {
        this.setContent(workerPos.x + dx, workerPos.y + dy, '@');
        this.setContent(workerPos.x, workerPos.y, ' ');
        this.stepCount++;
        return true;
      } else if (currentCell === '@' && nextCell === '.') {
        this.setContent(workerPos.x + dx, workerPos.y + dy, '+');
        this.setContent(workerPos.x, workerPos.y, ' ');
        this.stepCount++;
        return true;
      } else if (currentCell === '+' && nextCell === ' ') {
        this.setContent(workerPos.x + dx, workerPos.y + dy, '@');
        this.setContent(workerPos.x, workerPos.y, '.');
        this.stepCount++;
        return true;
      } else if (currentCell === '+' && nextCell === '.') {
        this.setContent(workerPos.x + dx, workerPos.y + dy, '+');
        this.setContent(workerPos.x, workerPos.y, '.');
        this.stepCount++;
        return true;
      }
    } else if (this.canPush(dx, dy)) {
      if (save) this.history.push({ matrix: this.matrix.map(row => [...row]), stepCount: this.stepCount });

      const boxX = workerPos.x + dx;
      const boxY = workerPos.y + dy;
      this.moveBox(boxX, boxY, dx, dy);
      const boxOldPosCell = this.getContent(boxX, boxY);
      this.setContent(workerPos.x, workerPos.y, currentCell === '+' ? '.' : ' ');
      this.setContent(workerPos.x + dx, workerPos.y + dy, boxOldPosCell === '.' ? '+' : '@');
      this.stepCount++;
      return true;
    }

    return false;
  }

  unmove(): boolean {
    if (this.history.length === 0) return false;

    const saved = this.history.pop()!;
    this.matrix = saved.matrix.map(row => [...row]);
    this.stepCount = saved.stepCount;
    return true;
  }

  canUndo(): boolean {
    return this.history.length > 0;
  }

  reset(matrix: CellType[][]): void {
    this.matrix = matrix.map(row => [...row]);
    this.history = [];
    this.stepCount = 0;
  }

  clone(): Game {
    const newGame = new Game(this.matrix, this.limit);
    newGame.history = this.history.map(h => ({ matrix: h.matrix.map(row => [...row]), stepCount: h.stepCount }));
    newGame.stepCount = this.stepCount;
    return newGame;
  }
}